import React from 'react';
import styled from 'styled-components';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'datetime-local' | 'time';
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  autoComplete?: string;
}

const StyledInput = styled.input<{
  $size: string;
  $error: boolean;
  $disabled: boolean;
}>`
  width: 100%;
  font-family: inherit;
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1.125rem';
      default: return '1rem';
    }
  }};
  line-height: 1.5;
  border: 1px solid ${props => props.$error ? props.theme.destructive : props.theme.border};
  border-radius: ${props => props.theme.radius};
  background-color: ${props => props.theme.input};
  color: ${props => props.theme.foreground};
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '0.375rem 0.75rem';
      case 'lg': return '0.75rem 1rem';
      default: return '0.5rem 0.75rem';
    }
  }};
  transition: ${props => props.theme.transitionSmooth};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'text'};

  &::placeholder {
    color: ${props => props.theme.mutedForeground};
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$error ? props.theme.destructive : props.theme.border};
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    background-color: ${props => props.theme.muted};
    cursor: not-allowed;
  }

  &:read-only {
    background-color: ${props => props.theme.muted};
  }
`;

export const Input: React.FC<InputProps> = ({
  type = 'text',
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  error = false,
  size = 'md',
  className,
  id,
  name,
  required,
  autoComplete,
  ...props
}) => {
  return (
    <StyledInput
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      $size={size}
      $error={error}
      $disabled={disabled}
      className={className}
      id={id}
      name={name}
      required={required}
      autoComplete={autoComplete}
      {...props}
    />
  );
}; 